/**
 * SAP Business One writeback state (สถานะการซิงค์เข้า SAP)
 *
 * The writer is the PSC Connector (etax-db repo), not this app: once a
 * document reaches @LaoEtaxStatus='approved' the connector posts it into SAP
 * B1 and writes back
 *
 *   @LaoEtaxSapSynced            true on success, false while queued / on failure
 *   @LaoEtaxSapSyncError         '' on success, the message on failure
 *   @LaoEtaxSapSyncedDocNum      echoes @LaoEtaxInvoiceNumber (what was PUSHED)
 *   @LaoEtaxSapSyncedQRCodeHash
 *   @DB.SBO_OJDT_transId         the journal entry it landed on
 *
 * Three states, which is one fewer than the raw fields suggest: a document the
 * connector has never looked at (@LaoEtaxSapSynced absent) and one it has
 * accepted but not yet posted (false, no error) are both PENDING here.
 *
 * documents/table.vue draws those two apart — a grey circle vs. a bare '-' —
 * because it lists every document, including the ones the connector will never
 * consider, so "untouched" there carries real information. On a page of
 * approved documents only, both mean the same thing to the reader: waiting on
 * the connector. They share one glyph and one filter bucket, and `sapIsTouched`
 * is exported so a tooltip can still say which of the two it is.
 *
 * table.vue keeps its own inline copy of this logic (laoSapState /
 * laoSapTooltip / sapSourceDocLabel, which predate this module). Move it onto
 * these helpers the next time that column is touched — do not add a third copy.
 */

export const SAP_SYNC_SYNCED = "synced";
export const SAP_SYNC_PENDING = "pending";
export const SAP_SYNC_FAILED = "failed";

// The keys double as the sort key of the SAP column, and they happen to sort
// failed -> pending -> synced, which puts the rows someone has to chase first.
export const SAP_SYNC_META = {
  failed: { label: "ซิงค์ไม่สำเร็จ", icon: "mdi-alert-circle", color: "error" },
  pending: { label: "ยังไม่ซิงค์", icon: "mdi-circle-outline", color: "#9e9e9e" },
  synced: { label: "ซิงค์แล้ว", icon: "mdi-check-circle", color: "#6ec39b" },
};

// The order a filter offers them in — the common case first, problems last.
// Deliberately not the sort order above.
export const SAP_SYNC_FILTER_ORDER = [SAP_SYNC_SYNCED, SAP_SYNC_PENDING, SAP_SYNC_FAILED];

/** Has the connector written @LaoEtaxSapSynced at all? */
export function sapIsTouched(data) {
  const synced = data["@LaoEtaxSapSynced"];
  return synced === true || synced === false;
}

/** The failure message, trimmed; '' when there is none. */
export function sapSyncError(data) {
  const raw = data["@LaoEtaxSapSyncError"];
  return typeof raw === "string" ? raw.trim() : "";
}

/**
 * One of SAP_SYNC_*, read off a raw Firestore document.
 *
 * Deliberately identical to table.vue's laoSapState, down to only counting an
 * error message as a failure when the boolean is present too: the two pages
 * must never disagree about a row. The connector always writes both.
 */
export function sapSyncState(data) {
  if (data["@LaoEtaxSapSynced"] === true) return SAP_SYNC_SYNCED;
  if (sapIsTouched(data) && sapSyncError(data)) return SAP_SYNC_FAILED;
  return SAP_SYNC_PENDING;
}

export function sapSyncLabel(state) {
  const meta = SAP_SYNC_META[state];
  return meta ? meta.label : state || "-";
}

// OJDT.TransType is the SAP document that POSTED the journal entry, and each
// Lao document type produces a different one. Calling all three "SAP JE" tells
// a user on the credit-note side nothing, so the label names the document.
const TRANS_TYPE_LABELS = {
  13: "ใบกำกับ (A/R Invoice)",
  14: "ใบลดหนี้ (A/R Credit Memo)",
  24: "ใบรับชำระ (Incoming Payment)",
};

export function sapSourceDocLabel(transType) {
  const known = TRANS_TYPE_LABELS[Number(transType)];
  if (known) return known;
  // Unknown or absent: say so rather than implying a type we never saw.
  return transType ? "SAP transType " + transType : "SAP";
}

/**
 * Tooltip for one table row. `row` carries the flattened fields a view builds
 * with the helpers above: sapState, sapTouched, sapError, sapTransId,
 * sapTransType.
 */
export function sapSyncTooltip(row) {
  if (row.sapState === SAP_SYNC_SYNCED) {
    // @DB.SBO_OJDT_transId, not @LaoEtaxSapSyncedDocNum: that field only
    // echoes back the eTax number that was pushed, so it says nothing about
    // where the entry landed. transId is the handle someone reconciling
    // against B1 actually looks up.
    if (!row.sapTransId) return "ซิงค์เข้า SAP แล้ว";
    return (
      "ซิงค์เข้า SAP แล้ว\n" + sapSourceDocLabel(row.sapTransType) + " — " + row.sapTransId
    );
  }
  if (row.sapState === SAP_SYNC_FAILED) {
    return (
      "ซิงค์เข้า SAP ไม่สำเร็จ:\n" +
      row.sapError +
      "\n\nสั่งซิงค์ใหม่ได้ที่หน้าเอกสาร — คลิกไอคอน SAP ในตาราง"
    );
  }
  return row.sapTouched
    ? "รอซิงค์เข้า SAP — ตัวเชื่อม PSC Connector รับเอกสารนี้ไว้แล้ว"
    : "ยังไม่ซิงค์เข้า SAP — ตัวเชื่อม PSC Connector ยังไม่ได้ดึงเอกสารนี้";
}

export default {
  SAP_SYNC_META,
  SAP_SYNC_FILTER_ORDER,
  sapSyncState,
  sapSyncLabel,
  sapSyncTooltip,
};
